import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Share2, Printer, LayoutTemplate, ChevronDown } from 'lucide-react';
import { trackEvent } from '../hooks/useAnonymousTracker';
import PosterA5 from './PosterA5';
import PosterA5Multi from './PosterA5Multi';

/**
 * A calm, print-friendly "Share this site" card designed for ISVAs,
 * IDVAs, social workers, SARC staff and community organisations to
 * hand out. Contains the site URL, a copy-to-clipboard control and
 * a scannable QR code. All actions are client-side and anonymous.
 *
 * The QR code and the copied / shared links carry a short ?src= tag
 * (qr, share-copy, share-native) so that arrivals via each channel
 * can be counted anonymously on the admin stats page. The tag is
 * stripped from the URL as soon as the visitor lands. The URL shown
 * to the reader on the card itself is always the clean, un-tagged
 * version.
 */
function withSrc(url, src) {
  try {
    const u = new URL(url);
    u.searchParams.set('src', src);
    return u.toString();
  } catch {
    return url;
  }
}

export default function ShareCard() {
  // Prefer the production canonical URL if provided, otherwise fall back
  // to the current origin so previews still work.
  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return 'https://thefullimpact.org';
    const origin = window.location.origin;
    // If we're on the emergent preview, still show the canonical prod URL
    if (/preview\.emergentagent\.com$/i.test(new URL(origin).hostname)) {
      return 'https://thefullimpact.org';
    }
    return origin;
  }, []);

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const toCopy = withSrc(shareUrl, 'share-copy');
    try {
      await navigator.clipboard.writeText(toCopy);
      setCopied(true);
      trackEvent('share-copy');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = toCopy;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        trackEvent('share-copy');
        setTimeout(() => setCopied(false), 1800);
      } catch {
        // no-op
      }
      document.body.removeChild(el);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'The Full Impact',
          text: 'A calm, anonymous UK guide for survivors of rape, sexual violence and domestic abuse.',
          url: withSrc(shareUrl, 'share-native'),
        });
        trackEvent('share-native');
      } catch {
        // user cancelled — do not count
      }
    } else {
      handleCopy();
    }
  };

  const handlePrint = () => {
    trackEvent('share-print');
    // Add a class that isolates just the share card in print CSS
    document.body.classList.add('tf-print-share');
    window.print();
    // Remove on next tick so the class doesn't stick if the user cancels
    setTimeout(() => document.body.classList.remove('tf-print-share'), 400);
  };

  const handlePrintPoster = () => {
    trackEvent('share-print-poster');
    document.body.classList.add('tf-print-poster');
    window.print();
    setTimeout(() => document.body.classList.remove('tf-print-poster'), 400);
  };

  const printMulti = (bodyClass, eventName) => {
    trackEvent(eventName);
    document.body.classList.add(bodyClass);
    window.print();
    setTimeout(() => document.body.classList.remove(bodyClass), 400);
  };

  const [posterMenuOpen, setPosterMenuOpen] = useState(false);
  const posterMenuRef = useRef(null);
  const posterButtonRef = useRef(null);

  useEffect(() => {
    if (!posterMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPosterMenuOpen(false);
        posterButtonRef.current?.focus();
      }
    };
    const onClickAway = (e) => {
      if (
        posterMenuRef.current &&
        !posterMenuRef.current.contains(e.target) &&
        posterButtonRef.current &&
        !posterButtonRef.current.contains(e.target)
      ) {
        setPosterMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClickAway);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClickAway);
    };
  }, [posterMenuOpen]);

  const posterOptions = [
    {
      key: 'single',
      label: '1 poster on A5 (landscape)',
      hint: 'One full-size poster, ideal for a noticeboard.',
      action: () => handlePrintPoster(),
    },
    {
      key: '2up-a4',
      label: '2 posters on A4 (portrait)',
      hint: 'Two full-size A5 posters, stacked. Standard home printer.',
      action: () => printMulti('tf-print-poster-2up-a4', 'share-print-poster-2up-a4'),
    },
    {
      key: '4up-a3',
      label: '4 posters on A3 (landscape)',
      hint: 'Four full-size A5 posters, 2×2. Needs an A3 printer.',
      action: () => printMulti('tf-print-poster-4up-a3', 'share-print-poster-4up-a3'),
    },
    {
      key: '4up-a4-mini',
      label: '4 small handouts on A4 (landscape)',
      hint: 'Four pocket-size handouts, cut in half for GP surgeries or IDVA drop-ins.',
      action: () => printMulti('tf-print-poster-4up-a4-mini', 'share-print-poster-4up-a4-mini'),
    },
  ];

  const displayUrl = shareUrl.replace(/^https?:\/\//, '');
  const qrUrl = withSrc(shareUrl, 'qr');
  const posterUrl = withSrc(shareUrl, 'qr');

  return (
    <>
    <section
      data-testid="share-card"
      className="tf-share-card rounded-2xl p-5 sm:p-6"
      style={{
        background: '#fff',
        border: '1.5px solid var(--tf-purple-light)',
        boxShadow: '0 6px 20px rgba(139,123,177,0.08)',
      }}
      aria-labelledby="share-card-title"
    >
      <div className="flex items-start gap-5 flex-col sm:flex-row">
        {/* QR block */}
        <div
          className="tf-share-qr flex-shrink-0 flex flex-col items-center gap-2 p-3 rounded-xl mx-auto sm:mx-0"
          style={{
            background: 'var(--tf-bg-secondary)',
            border: '1px solid var(--tf-card-border)',
          }}
        >
          <div
            className="p-2 rounded-lg bg-white"
            aria-hidden="true"
          >
            <QRCodeSVG
              value={qrUrl}
              size={112}
              level="M"
              includeMargin={false}
              bgColor="#ffffff"
              fgColor="#3F1F5B"
              data-testid="share-qr"
            />
          </div>
          <p
            className="font-body"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.2em',
              color: 'var(--tf-text-muted)',
              fontWeight: 700,
            }}
          >
            SCAN TO OPEN
          </p>
        </div>

        {/* Text + actions */}
        <div className="flex-1 min-w-0 w-full">
          <p
            className="font-body font-bold"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.24em',
              color: 'var(--tf-purple)',
            }}
          >
            SHARE THIS SITE
          </p>
          <h3
            id="share-card-title"
            className="font-heading mt-1"
            style={{
              fontSize: 'clamp(18px, 2vw, 22px)',
              color: 'var(--tf-text)',
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
            }}
          >
            Hand out a calm survivor guide.
          </h3>
          <p
            className="font-body mt-2"
            style={{
              fontSize: 13.5,
              color: 'var(--tf-text-secondary)',
              lineHeight: 1.55,
            }}
          >
            For ISVAs, IDVAs, SARC staff, social workers and community organisations —
            print this card, scan the QR, or share the link directly.
          </p>

          {/* URL row */}
          <div
            className="mt-4 flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5"
            style={{
              background: 'var(--tf-bg-secondary)',
              border: '1px solid var(--tf-card-border)',
            }}
          >
            <span
              className="font-body flex-1 min-w-0 truncate"
              data-testid="share-url"
              style={{
                fontSize: 13,
                color: 'var(--tf-text)',
                fontWeight: 600,
              }}
              title={shareUrl}
            >
              {displayUrl}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              data-testid="share-copy-button"
              aria-label={copied ? 'Link copied to clipboard' : 'Copy link to clipboard'}
              className="tf-no-print inline-flex items-center gap-1.5 px-3 py-2 rounded-full font-bold tf-focus"
              style={{
                background: copied ? 'var(--tf-purple)' : '#fff',
                color: copied ? '#fff' : 'var(--tf-purple)',
                border: '1px solid var(--tf-purple-light)',
                fontSize: 12,
                minHeight: 36,
              }}
            >
              {copied ? (
                <>
                  <Check size={13} strokeWidth={2.4} />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={13} strokeWidth={2.2} />
                  Copy
                </>
              )}
            </button>
          </div>

          {/* Secondary actions */}
          <div className="tf-no-print mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleNativeShare}
              data-testid="share-native-button"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold tf-focus"
              style={{
                background: 'var(--tf-purple)',
                color: '#fff',
                fontSize: 12.5,
                minHeight: 36,
              }}
            >
              <Share2 size={13} strokeWidth={2.2} />
              Share…
            </button>
            <button
              type="button"
              onClick={handlePrint}
              data-testid="share-print-button"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold tf-focus"
              style={{
                background: '#fff',
                color: 'var(--tf-purple)',
                border: '1px solid var(--tf-purple-light)',
                fontSize: 12.5,
                minHeight: 36,
              }}
            >
              <Printer size={13} strokeWidth={2.2} />
              Print this card
            </button>
            <div style={{ position: 'relative' }}>
              <button
                ref={posterButtonRef}
                type="button"
                onClick={() => setPosterMenuOpen((v) => !v)}
                data-testid="share-print-poster-button"
                aria-haspopup="menu"
                aria-expanded={posterMenuOpen}
                aria-label="Print poster options"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold tf-focus"
                style={{
                  background: '#fff',
                  color: 'var(--tf-purple)',
                  border: '1px solid var(--tf-purple-light)',
                  fontSize: 12.5,
                  minHeight: 36,
                }}
              >
                <LayoutTemplate size={13} strokeWidth={2.2} />
                Print poster
                <ChevronDown size={13} strokeWidth={2.2} />
              </button>
              {posterMenuOpen && (
                <div
                  ref={posterMenuRef}
                  role="menu"
                  aria-label="Print poster layouts"
                  data-testid="share-print-poster-menu"
                  className="tf-fade-up"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    zIndex: 60,
                    width: 320,
                    maxWidth: 'calc(100vw - 24px)',
                    background: '#fff',
                    border: '1px solid var(--tf-card-border)',
                    borderRadius: 14,
                    boxShadow: '0 20px 40px rgba(43, 22, 78, 0.14)',
                    padding: 6,
                  }}
                >
                  {posterOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      role="menuitem"
                      data-testid={`share-print-poster-option-${opt.key}`}
                      onClick={() => {
                        setPosterMenuOpen(false);
                        opt.action();
                      }}
                      className="w-full text-left rounded-lg tf-focus"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '10px 12px',
                        cursor: 'pointer',
                        color: 'var(--tf-text)',
                        display: 'block',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--tf-bg-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <p
                        className="font-body font-bold"
                        style={{ fontSize: 13, color: 'var(--tf-text)', margin: 0 }}
                      >
                        {opt.label}
                      </p>
                      <p
                        className="font-body"
                        style={{
                          fontSize: 11.5,
                          color: 'var(--tf-text-secondary)',
                          margin: '2px 0 0 0',
                          lineHeight: 1.4,
                        }}
                      >
                        {opt.hint}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <p
            className="font-body mt-3"
            style={{
              fontSize: 11.5,
              color: 'var(--tf-text-muted)',
              lineHeight: 1.55,
            }}
          >
            Free · Anonymous · No sign-up · Works offline once loaded.
          </p>
        </div>
      </div>
    </section>
    <PosterA5 url={posterUrl} />
    <PosterA5Multi url={posterUrl} count={2} layout="2up-a4" />
    <PosterA5Multi url={posterUrl} count={4} layout="4up-a3" />
    <PosterA5Multi url={posterUrl} count={4} layout="4up-a4-mini" />
    </>
  );
}
